// @spec BCP-STR-027
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useExerciseDetail } from '@/features/bcp/api/exercises';
import { ExerciseReport } from '@/features/bcp/components/ExerciseReport';

export const Route = createFileRoute('/_authenticated/bcp/exercises/$exerciseId/')({
  component: ExerciseDetailPage,
});

const OUTCOME_BADGE_VARIANT: Record<string, string> = {
  passed: 'default',
  partial: 'warning',
  failed: 'destructive',
};

const OUTCOME_I18N_KEY: Record<string, string> = {
  passed: 'exercises.outcomePassed',
  partial: 'exercises.outcomePartial',
  failed: 'exercises.outcomeFailed',
};

function ExerciseDetailPage() {
  const { t } = useTranslation('bcp');
  const { exerciseId } = Route.useParams();
  const navigate = useNavigate();
  const { data: exercise, isLoading, isError } = useExerciseDetail(exerciseId);

  const handleBack = () => {
    void navigate({ to: '/bcp/dashboard' });
  };

  const handleConduct = () => {
    void navigate({
      to: '/bcp/exercises/$exerciseId/conduct',
      params: { exerciseId },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !exercise) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-destructive">{t('exercises.failedToLoadExercise')}</p>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            {t('exercises.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Breadcrumb / Back nav */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('exercises.backToDashboard')}
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">
            {t('exercises.breadcrumbExercises', { name: exercise.name })}
          </span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{exercise.name}</h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {exercise.exerciseType}
              </Badge>
              <Badge variant="outline">
                {t(`exerciseStatuses.${exercise.status}`)}
              </Badge>
              {exercise.outcome && (
                <Badge
                  variant={
                    (OUTCOME_BADGE_VARIANT[exercise.outcome] ?? 'secondary') as
                      | 'destructive'
                      | 'default'
                      | 'secondary'
                  }
                >
                  {t(OUTCOME_I18N_KEY[exercise.outcome] ?? 'exercises.outcomePassed')}
                </Badge>
              )}
            </div>
          </div>
          {(exercise.status === 'planned' || exercise.status === 'in_progress') && (
            <Button onClick={handleConduct}>
              <PlayCircle className="mr-2 h-4 w-4" />
              {exercise.status === 'planned' ? t('exercises.conductExercise') : t('exercises.continueExercise')}
            </Button>
          )}
        </div>

        {/* Report (for all states) */}
        <ExerciseReport exercise={exercise} findings={exercise.findings} />
      </div>
    </div>
  );
}
